import { useIdleCallback } from '@/common/hooks/useIdleCallback'
import { SearchTypes } from '@rolebase/shared/model/search'
import { UseComboboxStateChange } from 'downshift'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchPerimeterProps } from '../circlePerimeter'
import incrementalSearch from '../incrementalSearch'
import { SearchItem } from '../searchTypes'

// Non-selectable id for the separator row inserted between in-perimeter and
// out-of-perimeter results (ORG-02 soft partition). Rendered as a disabled
// label row by SearchResultsList.
const perimeterSeparatorId = '__perimeter_separator__'

function sortByTitle(searchItems: SearchItem[]): SearchItem[] {
  return [...searchItems].sort((a, b) => a.title.localeCompare(b.title))
}

export function useSearch(
  items: SearchItem[],
  defaultEmpty: boolean,
  additionalTopItems?: SearchItem[],
  createItem?: boolean,
  // ORG-02: optional soft-partition perimeter, opt-in only for the circle
  // picker (CircleSearchButton/CircleSearchInput via CircleFormController).
  // When omitted, this hook behaves strictly as before (members/threads/
  // base-roles searches are unaffected).
  perimeter?: SearchPerimeterProps
) {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')

  // Filtered items using incremental search
  const filteredItems = useMemo(() => {
    let searchItems: SearchItem[]

    if (perimeter) {
      // ORG-02: soft partition. Empty field -> perimeter-only (no separator,
      // unless perimeter is "all" which is a flat, unrestricted list). While
      // typing -> in-perimeter items first, then a separator, then the rest
      // (still flat for "all", since isPartitioned is false in that case).
      if (searchText === '') {
        searchItems = defaultEmpty
          ? []
          : perimeter.isPartitioned
          ? sortByTitle(items.filter(perimeter.isInPerimeter))
          : sortByTitle(items)
      } else {
        const matched = incrementalSearch(items, searchText)
        if (perimeter.isPartitioned) {
          const inPerimeter = sortByTitle(
            matched.filter(perimeter.isInPerimeter)
          )
          const outPerimeter = sortByTitle(
            matched.filter((item) => !perimeter.isInPerimeter(item))
          )
          searchItems =
            outPerimeter.length > 0
              ? [
                  ...inPerimeter,
                  {
                    id: perimeterSeparatorId,
                    type: SearchTypes.Circle,
                    text: '',
                    title: perimeter.separatorLabel,
                    isSeparator: true,
                  },
                  ...outPerimeter,
                ]
              : inPerimeter
        } else {
          searchItems = sortByTitle(matched)
        }
      }
    } else {
      // Default behavior, strictly unchanged from before ORG-02.
      searchItems =
        searchText === ''
          ? defaultEmpty
            ? []
            : items
          : incrementalSearch(items, searchText)

      // Sort by title
      searchItems.sort((a, b) => a.title.localeCompare(b.title))
    }

    // Add Create item
    if (createItem && searchText.length >= 1) {
      return searchItems.concat({
        id: 'create',
        text: searchText,
        type: SearchTypes.CreateAction,
        title: t(`useSearch.create`, {
          name: searchText,
        }) as string,
      })
    }

    if (additionalTopItems && searchText === '') {
      searchItems.unshift(...additionalTopItems)
    }

    return searchItems
  }, [t, searchText, items, defaultEmpty, perimeter])

  // Search when input value changes
  const onInputValueChange = useIdleCallback(
    ({ inputValue }: UseComboboxStateChange<SearchItem>) => {
      setSearchText(inputValue || '')
    },
    []
  )

  return {
    filteredItems,
    onInputValueChange,
  }
}
